'use client';

import { ReactNode, useEffect, useRef, useSyncExternalStore } from 'react';
import ButtonComponent from '@spa/components/button/button/button.component';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import { RecoverySnapshot } from '@spa/lib/fastly-recovery/controller';
import styles from './fastly-recovery.module.scss';
import { verificationPath } from '@spa/lib/fastly-recovery/protocol';

export interface Props {
  readonly messages: {
    title: string;
    frameTitle: string;
    progress: string;
    captcha: string;
    cancel: string;
    cancelled: string;
    failed: string;
    timedOut: string;
    reset: string;
  };
}
const empty: RecoverySnapshot = { attemptId: null, status: 'waiting', outcome: null };
const serverSnapshot = (): RecoverySnapshot => empty;
const noSubscribe = (): (() => void) => (): void => {};

export default function FastlyRecoveryComponent({ messages }: Props): ReactNode {
  const controller = typeof window === 'undefined' ? undefined : window.fastlyActionRecovery;
  const state = useSyncExternalStore(
    controller?.subscribe ?? noSubscribe,
    controller?.getSnapshot ?? serverSnapshot,
    serverSnapshot
  );
  const dialogRef = useRef<HTMLDialogElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const visible = Boolean(state.attemptId || state.outcome);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    if (visible && dialog && !dialog.open) {
      dialog.showModal();
    }
    return (): void => {
      dialog?.close();
      if (previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }
    };
  }, [visible]);

  useEffect(() => {
    if (state.attemptId && frameRef.current && controller) {
      // Bind the source before navigation, so even immediate bootstrap messages are checked.
      controller.attachFrame(state.attemptId, frameRef.current.contentWindow);
      frameRef.current.src = `${verificationPath}#${state.attemptId}`;
    }
  }, [controller, state.attemptId]);

  const status =
    state.outcome === 'cancelled'
      ? messages.cancelled
      : state.outcome === 'timed-out'
        ? messages.timedOut
        : state.outcome
          ? messages.failed
          : state.status === 'captcha_prompted'
            ? messages.captcha
            : messages.progress;
  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="fastly-recovery-title"
      onCancel={(event): void => {
        event.preventDefault();
        if (state.outcome) {
          controller?.reset();
        } else {
          controller?.cancel();
        }
      }}
    >
      <h2 id="fastly-recovery-title">{messages.title}</h2>
      <p role="status" aria-live="polite">
        {status}
      </p>
      {state.attemptId ? (
        <div className={styles.verification} data-captcha={state.status === 'captcha_prompted'}>
          <iframe
            key={state.attemptId}
            ref={frameRef}
            title={messages.frameTitle}
            className={styles.frame}
          />
          {state.status !== 'captcha_prompted' ? (
            <div className={styles.loading} aria-hidden="true">
              <span className={styles.spinner} />
            </div>
          ) : null}
        </div>
      ) : null}
      {state.attemptId ? (
        <ButtonComponent
          buttonType={ButtonType.TERTIARY}
          onClick={controller?.cancel}
          text={messages.cancel}
        />
      ) : (
        <ButtonComponent
          buttonType={ButtonType.TERTIARY}
          onClick={controller?.reset}
          text={messages.reset}
        />
      )}
    </dialog>
  );
}
